import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Check, Lightbulb } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useAppSettings } from '../hooks/useAppSettings';
import ToggleSwitch from './ToggleSwitch';
import { round, formatNumber, calculateArea, GRAMS_PER_OZ } from '../utils/calculatorUtils';
import {
  lengthToDisplay,
  lengthFromDisplay,
  weightToDisplay,
  lengthUnitLabel,
  weightUnitLabel,
} from '../utils/unitConversion';
import { getDefaultReference } from '../utils/defaultReferenceConfig';
import { logUsageEvent } from '../utils/logUsageEvent';
import { gaEvent } from '../utils/analytics';
import copyToClipboard from '../utils/copyToClipboard';
import '../styles/HomeCalculator.css';

/**
 * Inline 3-field Basic Calculator for the Home Page (no login required).
 *
 * Behavior:
 *  - Calculate is disabled until Width, Length, and Count all have values (count starts at 1).
 *  - After Calculate: results show, Calculate becomes disabled, Copy becomes enabled.
 *  - Editing any of the 3 fields: hides results, re-enables Calculate, disables Copy.
 *  - Toggling units (in/mm, oz/g): updates results live WITHOUT requiring re-calculate.
 *  - Uses the admin default reference cookie as the silent icing ratio.
 */
const HomeCalculator = () => {
  const { user } = useAuth();
  const { settings: appSettings } = useAppSettings();

  // Unit preferences — shared with the (legacy) Basic Calculator via the same localStorage keys
  const [isMetric, setIsMetric] = useState(() => {
    try { return localStorage.getItem('basic_isMetric') === 'true'; } catch { return false; }
  });
  const [isMetricWeight, setIsMetricWeight] = useState(() => {
    try {
      const stored = localStorage.getItem('basic_isMetricWeight');
      return stored !== null ? stored === 'true' : false;
    } catch { return false; }
  });

  useEffect(() => {
    try { localStorage.setItem('basic_isMetric', String(isMetric)); } catch { /* noop */ }
  }, [isMetric]);
  useEffect(() => {
    try { localStorage.setItem('basic_isMetricWeight', String(isMetricWeight)); } catch { /* noop */ }
  }, [isMetricWeight]);

  // Silent reference cookie (canonical inches + grams) from admin settings, fallback to hardcoded default
  const referenceCookie = useMemo(() => {
    const drc = appSettings?.default_reference_cookie;
    if (drc && drc.width && drc.length && drc.weightNoIcing && drc.weight) {
      return {
        width: String(drc.width),
        length: String(drc.length),
        weightNoIcing: String(drc.weightNoIcing),
        weight: String(drc.weight),
      };
    }
    return getDefaultReference();
  }, [appSettings?.default_reference_cookie]);

  // Shape selection
  const [shape, setShape] = useState('rectangle');

  // Target inputs — stored canonical (inches), count as string. Count defaults to 1.
  const [target, setTarget] = useState({ width: '', length: '', diameter: '', count: '1' });
  const [hasCalculated, setHasCalculated] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const copyTimeoutRef = useRef(null);

  useEffect(() => () => { if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current); }, []);

  // Dimensions must be greater than zero, not just non-empty — typing 0 used to silently
  // enable Calculate and then do nothing when clicked.
  const hasValidDimensions = shape === 'circle'
    ? (parseFloat(target.diameter) || 0) > 0
    : (parseFloat(target.width) || 0) > 0 && (parseFloat(target.length) || 0) > 0;

  const countValid = target.count !== '' && (parseInt(target.count, 10) || 0) > 0;

  const calculateEnabled = hasValidDimensions && countValid && !hasCalculated;
  const copyEnabled = hasCalculated;

  // Button reads "Calculate" once dimensions are valid; otherwise it tells the user what's missing
  // instead of sitting there enabled and doing nothing when clicked.
  const calculateButtonLabel = hasValidDimensions
    ? 'Calculate'
    : (shape === 'circle' ? 'Enter a value in the diameter' : 'Enter a value in the width and length');

  // Live result — recomputes whenever inputs OR units change (so unit toggles update results without re-Calculate)
  const result = useMemo(() => {
    const refWidth = parseFloat(referenceCookie.width) || 0;
    const refLength = parseFloat(referenceCookie.length) || 0;
    const refWeightNoIcing = parseFloat(referenceCookie.weightNoIcing) || 0;
    const refWeight = parseFloat(referenceCookie.weight) || 0;

    const targetWidth = parseFloat(target.width) || 0;
    const targetLength = parseFloat(target.length) || 0;
    const targetDiameter = parseFloat(target.diameter) || 0;
    const targetCount = parseInt(target.count, 10) || 0;

    const refArea = calculateArea('rectangle', refWidth, refLength);
    const refIcingWeight = refWeight - refWeightNoIcing;
    let icingPerArea = 0;
    if (refArea > 0 && refIcingWeight > 0) icingPerArea = refIcingWeight / refArea;

    const targetArea = shape === 'circle'
      ? calculateArea('circle', 0, 0, targetDiameter)
      : calculateArea('rectangle', targetWidth, targetLength);
    const totalIcingGrams = targetArea * icingPerArea * targetCount;

    const dimensionValid = shape === 'circle' ? targetDiameter > 0 : (targetWidth > 0 && targetLength > 0);
    if (!(dimensionValid && targetCount > 0 && icingPerArea > 0)) {
      return { valid: false };
    }

    const wLabel = weightUnitLabel(isMetricWeight);
    const lenLabel = lengthUnitLabel(isMetric);
    const icingPerTargetGrams = targetArea * icingPerArea;
    const perCookie = `${formatNumber(weightToDisplay(icingPerTargetGrams, isMetricWeight))} ${wLabel}`;

    // Metric total
    let totalMetricFormatted;
    if (totalIcingGrams >= 1000) {
      const kg = Math.floor(totalIcingGrams / 1000);
      const g = round(totalIcingGrams % 1000, 1);
      totalMetricFormatted = `${kg} kg ${g} g (${formatNumber(totalIcingGrams)} g)`;
    } else {
      totalMetricFormatted = `${formatNumber(totalIcingGrams)} g`;
    }

    // Imperial total
    const totalIcingOz = totalIcingGrams / GRAMS_PER_OZ;
    let totalImperialFormatted;
    if (totalIcingOz >= 16) {
      const lb = Math.floor(totalIcingOz / 16);
      const oz = round(totalIcingOz % 16, 1);
      totalImperialFormatted = `${lb} lb ${oz} oz (${formatNumber(totalIcingOz)} oz)`;
    } else {
      totalImperialFormatted = `${formatNumber(totalIcingOz)} oz`;
    }

    const primaryTotal = isMetricWeight ? totalMetricFormatted : totalImperialFormatted;
    const secondaryTotal = isMetricWeight ? totalImperialFormatted : totalMetricFormatted;

    let sizeText;
    if (shape === 'circle') {
      const displayDiam = formatNumber(lengthToDisplay(targetDiameter, isMetric));
      sizeText = `${displayDiam} ${lenLabel} diameter`;
    } else {
      const displayTargetW = formatNumber(lengthToDisplay(targetWidth, isMetric));
      const displayTargetL = formatNumber(lengthToDisplay(targetLength, isMetric));
      sizeText = `${displayTargetW} x ${displayTargetL} ${lenLabel}`;
    }

    return {
      valid: true,
      count: targetCount,
      sizeText,
      perCookie,
      primaryTotal,
      secondaryTotal,
    };
  }, [target, shape, referenceCookie, isMetric, isMetricWeight]);

  // Field changes — convert display → canonical, then reset the calculated state
  const handleShapeChange = (newShape) => {
    setShape(newShape);
    setTarget(prev => ({ ...prev, width: '', length: '', diameter: '' }));
    setHasCalculated(false);
    setCopySuccess(false);
  };

  const handleLengthFieldChange = (field, displayValue) => {
    const canonical = lengthFromDisplay(displayValue, isMetric);
    setTarget(prev => ({ ...prev, [field]: canonical === '' ? '' : String(canonical) }));
    setHasCalculated(false);
    setCopySuccess(false);
  };

  const handleCountChange = (displayValue) => {
    // Keep digits only; allow empty while typing
    const cleaned = displayValue.replace(/[^0-9]/g, '');
    setTarget(prev => ({ ...prev, count: cleaned }));
    setHasCalculated(false);
    setCopySuccess(false);
  };

  const handleCalculate = () => {
    if (!calculateEnabled) return;
    setHasCalculated(true);
    logUsageEvent({ eventType: 'calculate', calculator: 'basic', user });
    gaEvent('calculate_click', { calculator: 'basic' });
  };

  // Enter key submits from any of the size/count fields, same as clicking Calculate
  const handleFieldKeyDown = (e) => {
    if (e.key === 'Enter') handleCalculate();
  };

  const handleCopy = useCallback(() => {
    if (!result.valid) return;
    const plainText =
      `For ${result.count} ${shape === 'circle' ? 'Round ' : ''}Cookie${result.count !== 1 ? 's' : ''} to Decorate: (${result.sizeText})\n` +
      `Each cookie needs: ${result.perCookie}\n\n` +
      `Total Icing Needed:\n${result.primaryTotal}\n${result.secondaryTotal}` +
      '\n\nPro Tip: This estimates flood icing. Add 1–2 extra cookies to your count in the event you have to scrape a cookie and start over. ' +
      'Don\'t forget to add an extra 1–3 oz for details, lettering, or florals.' +
      '\n\n---\nResults provided by TheIcingCalculator.com';

    logUsageEvent({ eventType: 'copy_results', calculator: 'basic', user });
    gaEvent('copy_results', { calculator: 'basic' });

    copyToClipboard(plainText).then(() => {
      setCopySuccess(true);
      if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
      copyTimeoutRef.current = setTimeout(() => setCopySuccess(false), 2000);
    }).catch(() => {
      setCopySuccess(true);
      if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
      copyTimeoutRef.current = setTimeout(() => setCopySuccess(false), 2000);
    });
  }, [result, user]);

  const showResults = hasCalculated && result.valid;

  return (
    <div className="home-calc">
      <p className="home-calc-eyebrow">QUICK CALCULATOR</p>
      <h2 className="home-calc-title">How much icing do you need?</h2>
      <p className="home-calc-sub">
        Enter a cookie size and how many you're decorating. Calculate your icing instantly.
        Perfect for a one-off bake, a gift, or a single order you need an answer for fast.
      </p>

      {/* Measurement units */}
      <div className="home-calc-units">
        <div className="home-calc-unit-group">
          <span className={!isMetric ? 'home-calc-unit-active' : ''}>inches</span>
          <ToggleSwitch isOn={isMetric} onToggle={(val) => setIsMetric(val)} />
          <span className={isMetric ? 'home-calc-unit-active' : ''}>mm</span>
        </div>
        <div className="home-calc-unit-group">
          <span className={!isMetricWeight ? 'home-calc-unit-active' : ''}>oz</span>
          <ToggleSwitch isOn={isMetricWeight} onToggle={(val) => setIsMetricWeight(val)} />
          <span className={isMetricWeight ? 'home-calc-unit-active' : ''}>grams</span>
        </div>
      </div>

      {/* Shape selector */}
      <div className="home-calc-shape-toggle">
        <button
          type="button"
          className={`home-calc-shape-btn ${shape === 'rectangle' ? 'active' : ''}`}
          onClick={() => handleShapeChange('rectangle')}
        >
          <svg width="18" height="14" viewBox="0 0 18 14" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <rect x="1" y="1" width="16" height="12" rx="1" stroke="currentColor" strokeWidth="2"/>
          </svg>
          Rectangle
        </button>
        <button
          type="button"
          className={`home-calc-shape-btn ${shape === 'circle' ? 'active' : ''}`}
          onClick={() => handleShapeChange('circle')}
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="2"/>
          </svg>
          Circle
        </button>
      </div>

      {/* Input fields */}
      <div className="home-calc-fields">
        {shape === 'rectangle' ? (
          <>
            <div className="home-calc-field">
              <label htmlFor="home-calc-width">Width <span className="home-calc-unit-label">({lengthUnitLabel(isMetric)})</span></label>
              <input
                id="home-calc-width"
                type="number"
                inputMode="decimal"
                min="0"
                step="0.01"
                placeholder="0"
                value={lengthToDisplay(target.width, isMetric)}
                onChange={(e) => handleLengthFieldChange('width', e.target.value)}
                onFocus={(e) => e.target.select()}
                onKeyDown={handleFieldKeyDown}
              />
            </div>
            <div className="home-calc-field">
              <label htmlFor="home-calc-length">Length <span className="home-calc-unit-label">({lengthUnitLabel(isMetric)})</span></label>
              <input
                id="home-calc-length"
                type="number"
                inputMode="decimal"
                min="0"
                step="0.01"
                placeholder="0"
                value={lengthToDisplay(target.length, isMetric)}
                onChange={(e) => handleLengthFieldChange('length', e.target.value)}
                onFocus={(e) => e.target.select()}
                onKeyDown={handleFieldKeyDown}
              />
            </div>
          </>
        ) : (
          <div className="home-calc-field home-calc-field-wide">
            <label htmlFor="home-calc-diameter">Diameter <span className="home-calc-unit-label">({lengthUnitLabel(isMetric)})</span></label>
            <input
              id="home-calc-diameter"
              type="number"
              inputMode="decimal"
              min="0"
              step="0.01"
              placeholder="0"
              value={lengthToDisplay(target.diameter, isMetric)}
              onChange={(e) => handleLengthFieldChange('diameter', e.target.value)}
              onFocus={(e) => e.target.select()}
              onKeyDown={handleFieldKeyDown}
            />
          </div>
        )}
        <div className="home-calc-field">
          <label htmlFor="home-calc-count">Number of Cookies</label>
          <input
            id="home-calc-count"
            type="number"
            inputMode="numeric"
            min="1"
            step="1"
            placeholder="1"
            value={target.count}
            onChange={(e) => handleCountChange(e.target.value)}
            onFocus={(e) => e.target.select()}
            onKeyDown={handleFieldKeyDown}
          />
        </div>
      </div>

      {/* Calculate button */}
      <button
        type="button"
        className="home-calc-btn"
        onClick={handleCalculate}
        disabled={!calculateEnabled}
        title={calculateEnabled ? 'Calculate icing needed' : (hasValidDimensions ? 'Enter width, length, and number of cookies' : calculateButtonLabel)}
      >
        {calculateButtonLabel}
      </button>

      {/* Results */}
      {showResults && (
        <div className="home-calc-results">
          <div className="home-calc-results-head">
            <h3>Your Icing Plan</h3>
            <button
              type="button"
              className={`home-calc-copy ${copySuccess ? 'copied' : ''}`}
              onClick={handleCopy}
              disabled={!copyEnabled}
            >
              {copySuccess ? (
                <span className="home-calc-copy-label">
                  <Check size={16} aria-hidden="true" />
                  Copied!
                </span>
              ) : 'Copy to Clipboard'}
            </button>
          </div>

          <div className="home-calc-result-row">
            <span className="home-calc-result-label">For {result.count} cookie{result.count !== 1 ? 's' : ''} to decorate</span>
            <span className="home-calc-result-value">{result.sizeText}</span>
          </div>
          <div className="home-calc-result-row">
            <span className="home-calc-result-label">Each cookie needs</span>
            <span className="home-calc-result-value">{result.perCookie}</span>
          </div>
          <div className="home-calc-result-total">
            <span className="home-calc-total-label">Total Icing Needed</span>
            <span className="home-calc-total-primary">{result.primaryTotal}</span>
            <span className="home-calc-total-secondary">{result.secondaryTotal}</span>
          </div>

          <div className="home-calc-tip">
            <span className="home-calc-tip-icon"><Lightbulb size={18} aria-hidden="true" /></span>
            <div>
              <strong>Pro Tip:</strong> This estimates flood icing. Add 1–2 extra cookies to your count in the event you have to scrape a cookie and start over.  Don't forget to add an extra 1–3 oz for details, lettering, or florals.
            </div>
          </div>
        </div>
      )}

      {/* Pro promo */}
      <div className="home-calc-pro">
        <div className="home-calc-pro-text">
          <strong>Need to plan more?</strong> The <span className="home-calc-pro-badge">PRO</span> Calculator handles
          multiple shapes, colors, and full orders — plus Excel &amp; PDF export. It's free.
        </div>
        <Link
          to="/pro"
          className="home-calc-pro-btn"
          onClick={() => gaEvent('pro_cta_click', { source: 'home_calculator' })}
        >
          Open the Pro Calculator →
        </Link>
      </div>
    </div>
  );
};

export default HomeCalculator;
